import { AlertTriangle } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { ImageFocusSelect } from '@/components/ImageFocusSelect';

interface OrientationMismatchDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  imageUrl: string;
  preferredRatio: 'landscape' | 'portrait';
  imageFocus: string;
  onImageFocusChange: (value: string) => void;
  isSquare?: boolean;
}

export function OrientationMismatchDialog({
  open,
  onOpenChange,
  imageUrl,
  preferredRatio,
  imageFocus,
  onImageFocusChange,
  isSquare = false,
}: OrientationMismatchDialogProps) {
  const previewAspectClass = preferredRatio === 'portrait' ? 'aspect-[3/4]' : 'aspect-[3/2]';
  const description = isSquare
    ? `Your photo is square but it will be displayed in ${preferredRatio}. The center will be used, which may crop parts of the image. Use the focus control below to choose which part stays visible.`
    : preferredRatio === 'landscape'
      ? 'Your photo is portrait but it will be displayed in landscape. The center will be used, which usually cuts off heads. Use the focus control below to choose which part stays visible.'
      : 'Your photo is landscape but it will be displayed in portrait. The center will be used, which may cut off the sides. Use the focus control below to choose which part stays visible.';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size="medium">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-amber-500" />
            Image orientation mismatch
          </DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4 px-4 sm:px-6 py-4">
          <div className="overflow-hidden rounded-md border">
            <img
              src={imageUrl}
              alt="Upload preview"
              className={`${previewAspectClass} w-full object-cover`}
              style={{ objectPosition: imageFocus || 'center' }}
            />
          </div>

          <ImageFocusSelect value={imageFocus} onChange={onImageFocusChange} />
        </div>

        <DialogFooter>
          <Button type="button" onClick={() => onOpenChange(false)}>
            Got it
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
